import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Facebook, Instagram } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const STORAGE_KEY = 'vent_social_media_links';

interface SocialLinks {
  facebook: string;
  instagram: string;
  x: string;
  tiktok: string;
  linkedin: string;
}

// Default social media links - used when nothing is stored or stored data is invalid
const DEFAULT_LINKS: SocialLinks = {
  facebook: 'https://www.facebook.com/profile.php?id=61585575922815',
  instagram: 'https://www.instagram.com/the.vent.app/',
  x: 'https://x.com/TheVentAppX',
  tiktok: '',
  linkedin: 'https://linkedin.com/company/the-vent-app',
};

// Helper function to ensure URL has protocol
const ensureProtocol = (url: string): string => {
  if (!url || url.trim() === '') return '';
  const trimmedUrl = url.trim();
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  return `https://${trimmedUrl}`;
};

// Validate that an object has valid social links shape
const isValidSocialLinks = (obj: unknown): obj is SocialLinks => {
  if (!obj || typeof obj !== 'object') return false;
  const links = obj as Record<string, unknown>;
  return (
    typeof links.facebook === 'string' &&
    typeof links.instagram === 'string' &&
    typeof links.x === 'string'
  );
};

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const LinkedInIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

export const getSocialMediaLinks = (): SocialLinks => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (isValidSocialLinks(parsed)) {
        // Normalize URLs on read and use defaults for empty values
        return {
          facebook: ensureProtocol(parsed.facebook) || DEFAULT_LINKS.facebook,
          instagram: ensureProtocol(parsed.instagram) || DEFAULT_LINKS.instagram,
          x: ensureProtocol(parsed.x) || DEFAULT_LINKS.x,
          tiktok: ensureProtocol(parsed.tiktok || '') || DEFAULT_LINKS.tiktok,
          linkedin: ensureProtocol(parsed.linkedin || '') || DEFAULT_LINKS.linkedin,
        };
      }
    }
  } catch (e) {
    console.error('Error reading social media links:', e);
  }
  return DEFAULT_LINKS;
};

const SocialMediaSettings = () => {
  const { toast } = useToast();
  const [links, setLinks] = useState<SocialLinks>({
    facebook: '',
    instagram: '',
    x: '',
    tiktok: '',
    linkedin: '',
  });

  useEffect(() => {
    setLinks(getSocialMediaLinks());
  }, []);

  const isValidUrl = (url: string): boolean => {
    if (!url || url.trim() === '') return true; // empty is ok
    return /^https?:\/\/.+/.test(url.trim());
  };

  const handleSave = () => {
    // Validate all non-empty URLs
    const allLinks = [links.facebook, links.instagram, links.x, links.tiktok, links.linkedin];
    const invalidUrl = allLinks.find(url => url.trim() !== '' && !isValidUrl(url));
    if (invalidUrl) {
      toast({
        variant: 'destructive',
        title: 'Invalid URL',
        description: 'Please enter valid URLs starting with http:// or https://',
      });
      return;
    }

    try {
      const normalizedLinks = {
        facebook: ensureProtocol(links.facebook),
        instagram: ensureProtocol(links.instagram),
        x: ensureProtocol(links.x),
        tiktok: ensureProtocol(links.tiktok),
        linkedin: ensureProtocol(links.linkedin),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedLinks));
      setLinks(normalizedLinks);
      // Dispatch custom event for same-tab updates
      window.dispatchEvent(new Event('social-media-updated'));
      toast({
        title: 'Saved',
        description: 'Social media links have been updated.',
      });
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save social media links.',
      });
    }
  };

  const handleClearAll = () => {
    localStorage.removeItem(STORAGE_KEY);
    setLinks({ facebook: '', instagram: '', x: '', tiktok: '', linkedin: '' });
    window.dispatchEvent(new Event('social-media-updated'));
    toast({
      title: 'Cleared',
      description: 'All social media links have been cleared. Enter new URLs and save.',
    });
  };

  return (
    <Card className="border-primary/20 shadow-soft">
      <CardHeader>
        <CardTitle className="text-lg">Social Media Links</CardTitle>
        <CardDescription>
          Configure the social media links displayed in the footer
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="facebook" className="flex items-center gap-2">
            <Facebook className="h-4 w-4" />
            Facebook URL
          </Label>
          <Input
            id="facebook"
            placeholder="https://facebook.com/yourpage"
            value={links.facebook}
            onChange={(e) => setLinks({ ...links, facebook: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="instagram" className="flex items-center gap-2">
            <Instagram className="h-4 w-4" />
            Instagram URL
          </Label>
          <Input
            id="instagram"
            placeholder="https://instagram.com/yourpage"
            value={links.instagram}
            onChange={(e) => setLinks({ ...links, instagram: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="x" className="flex items-center gap-2">
            <XIcon className="h-4 w-4" />
            X (Twitter) URL
          </Label>
          <Input
            id="x"
            placeholder="https://x.com/yourpage"
            value={links.x}
            onChange={(e) => setLinks({ ...links, x: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="tiktok" className="flex items-center gap-2">
            <TikTokIcon className="h-4 w-4" />
            TikTok URL
          </Label>
          <Input
            id="tiktok"
            placeholder="https://tiktok.com/@yourpage"
            value={links.tiktok}
            onChange={(e) => setLinks({ ...links, tiktok: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="linkedin" className="flex items-center gap-2">
            <LinkedInIcon className="h-4 w-4" />
            LinkedIn URL
          </Label>
          <Input
            id="linkedin"
            placeholder="https://linkedin.com/company/yourpage"
            value={links.linkedin}
            onChange={(e) => setLinks({ ...links, linkedin: e.target.value })}
          />
        </div>

        <div className="flex gap-2">
          <Button onClick={handleSave} className="flex-1">
            Save Social Media Links
          </Button>
          <Button onClick={handleClearAll} variant="outline" className="text-destructive border-destructive hover:bg-destructive/10">
            Clear All
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SocialMediaSettings;
